import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ARTIST, SPOTIFY_ARTIST_EMBED } from "@/lib/constants";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

export const SpotifyFeature = () => {
  return (
    <section
      id="listen"
      data-testid="spotify-feature-section"
      className="relative w-full bg-bg py-24 md:py-32 border-t border-[color:var(--line)]"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10">
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold">
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              Now Playing
            </p>
            <h2
              data-testid="spotify-feature-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ink"
            >
              Listen on{" "}
              <span className="font-editorial text-gold-hi">Spotify</span>
            </h2>
          </motion.div>
          <motion.p
            variants={fadeUp}
            className="max-w-md text-sm text-muted leading-relaxed"
          >
            Stream every release, save favorites and follow the journey.
          </motion.p>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8"
        >
          {/* Side panel */}
          <div className="lg:col-span-4 relative flex flex-col justify-between overflow-hidden rounded-sm border border-[color:var(--gold)]/30 bg-gradient-to-br from-[color:var(--gold)]/[0.08] via-transparent to-transparent p-8 md:p-10">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-gold">
                Official Artist Page
              </p>
              <h3 className="mt-4 font-display text-3xl md:text-4xl font-bold uppercase tracking-tight text-ink">
                Irwan Craw
              </h3>
              <p className="mt-3 font-editorial text-lg text-[color:var(--gold-hi)]/90">
                The full catalog · latest drops · playlists
              </p>
              <p className="mt-6 text-sm text-muted leading-relaxed">
                Featured across Afro House, Deep House, Melodic House and
                Latin House curated selections.
              </p>
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <a
                data-testid="spotify-open-button"
                href={ARTIST.spotifyArtistUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="group inline-flex items-center justify-between gap-3 rounded-full bg-[color:var(--gold)] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.28em] text-[#0a0a0a] transition-all duration-500 hover:bg-[color:var(--gold-hi)] hover:shadow-[0_0_40px_rgba(201,169,97,0.35)]"
              >
                Open in Spotify
                <ArrowUpRight
                  size={14}
                  className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
              <a
                data-testid="apple-artist-button"
                href={ARTIST.appleMusicArtist}
                target="_blank"
                rel="noreferrer noopener"
                className="group inline-flex items-center justify-between gap-3 rounded-full border border-[color:var(--gold)]/40 bg-transparent px-5 py-3 text-[10px] font-bold uppercase tracking-[0.28em] text-[color:var(--gold-hi)] transition-all duration-500 hover:bg-white/[0.04] hover:border-[color:var(--gold)]"
              >
                Find on Apple Music
                <ArrowUpRight size={14} />
              </a>
            </div>
          </div>

          {/* Spotify embed */}
          <div className="lg:col-span-8 rounded-sm border border-[color:var(--line)] bg-bg-2 p-3 md:p-4">
            <div className="relative overflow-hidden rounded-sm">
              <iframe
                data-testid="spotify-artist-iframe"
                title="IRWAN CRAW on Spotify"
                src={SPOTIFY_ARTIST_EMBED}
                width="100%"
                height="480"
                frameBorder="0"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="block"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
